import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import { modals } from '@mantine/modals'

// const INACTIVE_TIME = 10 * 1000 // 디버깅용 10초
const INACTIVE_TIME = 30 * 60 * 1000 // 30분

export const useAuthStore = create(
  persist(
    (set, get) => {
      let timeoutRef = null

      // 자동 로그아웃 실행
      const performAutoLogout = () => {
        const { signOut } = get()
        signOut()

        modals.open({
          children: (
            <div style={{ textAlign: 'center' }}>
              <img
                src="/assets/rabbit-blue-monster-cry.png"
                alt="Logout"
                style={{ width: '80px', marginBottom: '16px' }}
              />
              <p style={{ fontSize: '16px', marginBottom: '20px' }}>
                You have been automatically logged out <br />
                due to 30 minutes of inactivity.
              </p>
              <button
                onClick={() => {
                  modals.closeAll()
                  // navigate() 대신 전체 페이지 이동 — 오래 열려있던 탭이 예전
                  // JS 번들을 계속 쓰는 것을 방지 (로그아웃 시 최신 빌드로 갱신)
                  window.location.href = '/'
                }}
                style={{
                  background: '#0e2a4f',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                }}
              >
                OK
              </button>
            </div>
          ),
          centered: true,
          withCloseButton: false,
          closeOnClickOutside: false,
          closeOnEscape: false,
        })
      }

      const resetAutoLogoutTimer = () => {
        const { isLoggedIn } = get()

        if (!isLoggedIn) return

        if (timeoutRef) {
          clearTimeout(timeoutRef)
        }
        timeoutRef = setTimeout(() => {
          performAutoLogout()
        }, INACTIVE_TIME)
      }

      const setupActivityDetection = () => {
        if (typeof window === 'undefined') return

        const events = [
          'mousedown',
          'mousemove',
          'keypress',
          'scroll',
          'touchstart',
          'click',
        ]

        const activityHandler = () => {
          const { isLoggedIn } = get()
          if (isLoggedIn) {
            resetAutoLogoutTimer()
          }
        }

        events.forEach((event) => {
          document.addEventListener(event, activityHandler, true)
        })

        resetAutoLogoutTimer()

        return () => {
          events.forEach((event) => {
            document.removeEventListener(event, activityHandler, true)
          })
          if (timeoutRef) clearTimeout(timeoutRef)
        }
      }

      return {
        isLoggedIn: false,
        user: null,
        keepLoggedIn: false,
        currentCenter: null,
        isViewingAsManager: false,

        signIn: (user, keepLoggedIn) => {
          set({
            isLoggedIn: true,
            user,
            keepLoggedIn,
          })

          if (keepLoggedIn) {
            sessionStorage.setItem(
              'auth-storage',
              JSON.stringify({
                state: {
                  isLoggedIn: true,
                  user,
                  keepLoggedIn: true,
                },
                version: 0,
              })
            )
          }

          setupActivityDetection()
        },

        signOut: () => {
          if (timeoutRef) clearTimeout(timeoutRef)

          set({
            isLoggedIn: false,
            user: null,
            keepLoggedIn: false,
            currentCenter: null,
            isViewingAsManager: false,
          })
          sessionStorage.removeItem('auth-storage')
        },

        setKeepLoggedIn: (keep) => {
          set({ keepLoggedIn: keep })
        },

        updateUser: (updatedUser) =>
          set((state) => ({
            user: updatedUser,
            isLoggedIn: state.isLoggedIn,
          })),

        updateUserPartial: (updates) =>
          set((state) => ({
            user: state.user ? { ...state.user, ...updates } : null,
          })),

        switchToAdmin: () =>
          set((state) => ({
            user: state.user ? { ...state.user, STATUS: 'A' } : null,
            currentCenter: null,
            isViewingAsManager: false,
          })),

        viewManagerPage: (centerData) => {
          set({
            isViewingAsManager: true,
            currentCenter: centerData,
          })
        },

        backToAdmin: () => {
          set({
            isViewingAsManager: false,
            currentCenter: null,
          })
        },

        resetAutoLogoutTimer,
        setupActivityDetection,
      }
    },
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        isLoggedIn: state.isLoggedIn,
        user: state.user,
        keepLoggedIn: state.keepLoggedIn,
        currentCenter: state.currentCenter,
        isViewingAsManager: state.isViewingAsManager,
      }),
      onRehydrateStorage: () => {
        return (state) => {
          if (state?.isLoggedIn) {
            state.setupActivityDetection?.()
          }
        }
      },
    }
  )
)
